import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, HardHat, Users, CalendarDays, BarChart3, Plane, UserCog, LogOut, Database, Briefcase, Menu, X, Sunrise, Palette } from 'lucide-react';
import { Button } from './ui/button';
import { usePermissions } from './PermissionsProvider';

export default function Sidebar({ user, setUser }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(true);
  const { canView, canEdit } = usePermissions();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenant_branding');
    setUser(null);
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsExpanded(!isExpanded);
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/', module: 'dashboard' },
    { icon: HardHat, label: 'Equipment Database', path: '/equipment', module: 'equipment_database' },
    { icon: Users, label: 'Personnel Database', path: '/personnel', module: 'personnel_database' },
    { icon: Briefcase, label: 'Projects', path: '/projects', module: 'projects_database' },
    { icon: CalendarDays, label: 'Equipment Planner', path: '/equipment-planner', module: 'equipment_planner' },
    { icon: CalendarDays, label: 'Personnel Planner', path: '/personnel-planner', module: 'personnel_planner' },
    { icon: Plane, label: 'MOB Tracker', path: '/mob-tracker', module: 'mob_tracker' },
    { icon: BarChart3, label: 'Reports', path: '/reports', module: 'reports' },
  ].filter(item => canView(item.module));

  if (canView('data_management')) {
    menuItems.push({ icon: Database, label: 'Data Management', path: '/data-management' });
  }
  if (canView('user_management') || canEdit('user_management')) {
    menuItems.push({ icon: UserCog, label: 'Users', path: '/users' });
  }
  if (canEdit('user_management')) {
    menuItems.push({ icon: Palette, label: 'Branding', path: '/branding' });
  }

  return (
    <div 
      className={`${isExpanded ? 'w-64' : 'w-20'} bg-slate-900 text-white flex flex-col transition-all duration-300`} 
      data-testid="sidebar"
    >
      <div className={`${isExpanded ? 'p-6' : 'p-4'} border-b border-slate-800`}>
        {/* Expanded state */}
        {isExpanded ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-2 rounded-lg">
                <Sunrise className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold tracking-tight">EQUIPR Horizon</h1>
                <p className="text-xs text-slate-400 font-mono uppercase tracking-wider">{user?.role || 'User'}</p>
              </div>
            </div>
            <Button
              onClick={toggleSidebar}
              data-testid="toggle-sidebar"
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:bg-slate-800 hover:text-white p-2"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        ) : (
          /* Collapsed state - centered vertically */
          <div className="flex flex-col items-center gap-3">
            <div className="bg-gradient-to-r from-slate-700 to-slate-800 p-2 rounded-lg">
              <Sunrise className="w-6 h-6 text-amber-400" />
            </div>
            <Button
              onClick={toggleSidebar}
              data-testid="toggle-sidebar"
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:bg-slate-800 hover:text-white p-2"
            >
              <Menu className="w-5 h-5" />
            </Button>
          </div>
        )}
      </div>
      
      <nav className="flex-1 p-4 space-y-1">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={`flex items-center ${isExpanded ? 'gap-3 px-4' : 'justify-center px-2'} py-3 rounded-md transition-all ${
                isActive
                  ? 'bg-amber-500 text-white shadow-sm'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-white'
              }`}
              title={!isExpanded ? item.label : ''}
            >
              <Icon className="w-5 h-5 flex-shrink-0" />
              {isExpanded && <span className="text-sm font-medium">{item.label}</span>}
            </Link>
          );
        })}
      </nav>

      <div className={`${isExpanded ? 'p-4' : 'p-2'} border-t border-slate-800`}>
        {isExpanded && (
          <div className="mb-3 px-2">
            <p className="text-sm font-medium text-white truncate">{user?.email}</p>
          </div>
        )}
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="ghost"
          className={`w-full ${isExpanded ? 'justify-start' : 'justify-center'} text-slate-300 hover:bg-slate-800 hover:text-white`}
          title={!isExpanded ? 'Logout' : ''}
        >
          <LogOut className={`w-5 h-5 ${isExpanded ? 'mr-3' : ''}`} />
          {isExpanded && 'Logout'}
        </Button>
        {isExpanded && (
          <div className="text-center pt-2 border-t border-slate-700/50 mt-2">
            <span className="text-[10px] text-slate-500" data-testid="app-version">v{process.env.REACT_APP_VERSION || '0.0.0'}</span>
          </div>
        )}
      </div>
    </div>
  );
}